import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, createApiClient } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Folder,
  Plus,
  Trash,
  PencilSimple,
  SpinnerGap,
  MapPin,
  X,
  Check,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const Collections = () => {
  const { token } = useAuth();
  const api = createApiClient(token);
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchCollections(); }, []);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const res = await api.get('/collections');
      setCollections(res.data.collections || []);
    } catch { toast.error('Failed to load collections'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post('/collections', { name: newName.trim(), description: newDesc.trim() });
      toast.success('Collection created');
      setNewName('');
      setNewDesc('');
      setShowCreate(false);
      fetchCollections();
    } catch { toast.error('Failed to create collection'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/collections/${id}`);
      toast.success('Collection deleted');
      fetchCollections();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) {
    return (
      <div className="container-app py-8 pb-8 flex justify-center pt-32">
        <SpinnerGap className="w-8 h-8 animate-spin text-[#A94438]" />
      </div>
    );
  }

  return (
    <div className="container-app py-8 pb-8" data-testid="collections-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#1A1918]" data-testid="collections-title">
            Collections
          </h1>
          <p className="text-[#7A7873] mt-1">Group your restaurants into curated lists</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="btn-primary"
          data-testid="create-collection-btn"
        >
          <Plus weight="bold" className="w-5 h-5 mr-1" />
          New
        </Button>
      </div>

      {/* Create form */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-2xl border border-[#EAE8E3] bg-white p-5 mb-6 overflow-hidden"
            data-testid="create-collection-form"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-[#1A1918]">New Collection</h3>
              <button onClick={() => setShowCreate(false)}>
                <X className="w-5 h-5 text-[#7A7873]" />
              </button>
            </div>
            <Input
              placeholder="Collection name (e.g. Date Night Spots)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="mb-3 rounded-xl"
              data-testid="collection-name-input"
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            />
            <Input
              placeholder="Description (optional)"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              className="mb-4 rounded-xl"
              data-testid="collection-desc-input"
            />
            <Button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              className="w-full btn-primary"
              data-testid="save-collection-btn"
            >
              {creating ? <SpinnerGap className="w-5 h-5 animate-spin" /> : <>
                <Check weight="bold" className="w-5 h-5 mr-1" /> Create Collection
              </>}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {collections.length === 0 && !showCreate && (
        <div className="empty-state">
          <Folder weight="duotone" className="empty-state-icon" />
          <h3 className="empty-state-title">No collections yet</h3>
          <p className="empty-state-text">Create lists like "Date Night Spots" or "Best Breakfasts"</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {collections.map((coll) => (
          <motion.div
            key={coll.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-[#EAE8E3] bg-white overflow-hidden hover:shadow-md transition-shadow"
            data-testid={`collection-card-${coll.id}`}
          >
            {/* Preview grid */}
            <div className="h-32 bg-[#F9F8F5] grid grid-cols-2 gap-px">
              {(coll.preview_restaurants || []).map((r, i) => (
                <div key={i} className="bg-[#EAE8E3] flex items-center justify-center p-2">
                  <span className="text-xs text-[#7A7873] font-medium text-center line-clamp-2">{r.name}</span>
                </div>
              ))}
              {Array.from({ length: Math.max(0, 4 - (coll.preview_restaurants || []).length) }).map((_, i) => (
                <div key={`empty-${i}`} className="bg-[#F9F8F5]" />
              ))}
            </div>

            <div className="p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading font-semibold text-[#1A1918]">
                  {coll.emoji && <span className="mr-1">{coll.emoji}</span>}
                  {coll.name}
                </h3>
                <button
                  onClick={(e) => { e.preventDefault(); handleDelete(coll.id); }}
                  className="text-[#9A9590] hover:text-[#A94438] transition-colors"
                  data-testid={`delete-collection-${coll.id}`}
                >
                  <Trash weight="duotone" className="w-4 h-4" />
                </button>
              </div>
              {coll.description && (
                <p className="text-sm text-[#7A7873] mb-2 line-clamp-1">{coll.description}</p>
              )}
              <p className="text-xs text-[#9A9590]">
                {coll.restaurant_count} restaurant{coll.restaurant_count !== 1 ? 's' : ''}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Collections;
